// CompareContext.tsx — Client-only compare set (max 5 players) with localStorage persistence.

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

const MAX_COMPARE = 5
const STORAGE_KEY = 'hooproster:compare'

interface CompareContextValue {
  slugs: string[]
  add: (slug: string) => { success: boolean; reason?: string }
  remove: (slug: string) => void
  toggle: (slug: string) => void
  clear: () => void
  has: (slug: string) => boolean
  count: number
  set: (slugs: string[]) => void // Bulk update (for URL hydration)
  hydrated: boolean // localStorage hydration complete
}

const CompareContext = createContext<CompareContextValue | null>(null)

export function CompareProvider({ children }: { children: ReactNode }) {
  const [slugs, setSlugs] = useState<string[]>([])
  const [hydrated, setHydrated] = useState(false)

  // Hydrate from localStorage on mount.
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed)) {
          setSlugs(parsed.slice(0, MAX_COMPARE))
        }
      }
    } catch {
      // Tolerate malformed storage; never crash.
    }
    setHydrated(true)
  }, [])

  // Persist to localStorage on change (skip until hydrated).
  useEffect(() => {
    if (!hydrated) return
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(slugs))
    } catch {
      // Tolerate quota exceeded, etc.
    }
  }, [slugs, hydrated])

  const add = (slug: string): { success: boolean; reason?: string } => {
    if (slugs.includes(slug)) return { success: false, reason: 'Already in compare' }
    if (slugs.length >= MAX_COMPARE) return { success: false, reason: `Comparison full (${MAX_COMPARE} max)` }
    setSlugs((prev) => [...prev, slug])
    return { success: true }
  }

  const remove = (slug: string) => {
    setSlugs((prev) => prev.filter((s) => s !== slug))
  }

  const toggle = (slug: string) => {
    if (slugs.includes(slug)) {
      remove(slug)
    } else {
      add(slug)
    }
  }

  const clear = () => {
    setSlugs([])
  }

  const has = (slug: string): boolean => {
    return slugs.includes(slug)
  }

  const set = (newSlugs: string[]) => {
    const next = newSlugs.slice(0, MAX_COMPARE)
    // Bail on identical content — callers may re-invoke with the same slugs
    // (e.g. URL-sync effects); returning prev lets React skip the re-render
    // and prevents update loops.
    setSlugs((prev) =>
      prev.length === next.length && prev.every((s, i) => s === next[i]) ? prev : next
    )
  }

  return (
    <CompareContext.Provider value={{ slugs, add, remove, toggle, clear, has, count: slugs.length, set, hydrated }}>
      {children}
    </CompareContext.Provider>
  )
}

export function useCompare() {
  const ctx = useContext(CompareContext)
  if (!ctx) throw new Error('useCompare must be used within CompareProvider')
  return ctx
}
